import type {
  SequenceObjective,
  SequenceOrderResult,
} from "../api-contracts/simulations-sequence.js";
import type { ObjectiveMetricView } from "./sim-objectives.js";

// REGISTRY DE OBJETIVOS DA SEQUÊNCIA (fonte única, pura, consumida pelo worker
// [ranqueamento dos candidatos de fila] e pela web [exibição/deltas]).
// Arquivo SEPARADO de sim-objectives.ts de propósito — é a segregação por `kind`:
// - lote PARAMS ranqueia sobre ObjectiveEval (OEE/produção/esforço de um candidato de máquina);
// - lote SEQUENCE ranqueia sobre a fila inteira (perOrder + totalSetupS), outra grandeza.
// Misturar os dois num Record só levaria a estratégias que ignoram metade dos campos.
// Os três objetivos aqui são "MENOR é melhor".

/** Agregados de um candidato de sequência — derivados de `results.perOrder`/`totalSetupS`. */
export interface SequenceObjectiveEval {
  /** Σ atraso (s) das ordens COM prazo. Adiantamento NÃO compensa atraso (clamp em 0). */
  totalLatenessS: number;
  /** Σ setup (s) pago nas trocas de produto da sequência. */
  totalSetupS: number;
  /** Fim da última ordem (s desde a âncora); +Infinity se alguma ordem não concluiu. */
  makespanS: number;
  /** Todas as ordens concluíram dentro do horizonte. */
  completed: boolean;
}

export interface SequenceObjectiveStrategy {
  id: SequenceObjective;
  label: string;
  /** Métrica-título do candidato para este objetivo (exibição). */
  metric(e: SequenceObjectiveEval): ObjectiveMetricView;
  /** Ordena candidatos "melhor primeiro" (total, determinístico; incompletos ao fim). */
  compare(a: SequenceObjectiveEval, b: SequenceObjectiveEval): number;
  /** O candidato é ganho real sobre a fila atual? (senão o lote fecha NO_GAIN). */
  beatsBaseline(candidate: SequenceObjectiveEval, baseline: SequenceObjectiveEval): boolean;
}

/**
 * Agrega o resultado por ordem do motor nos termos dos objetivos. Uma ordem que não concluiu
 * no horizonte torna o makespan indefinido (+Infinity) — parcial explícito, nunca "zero".
 */
export function evaluateSequence(
  perOrder: ReadonlyArray<SequenceOrderResult>,
  totalSetupS: number
): SequenceObjectiveEval {
  let totalLatenessS = 0;
  let lastEndS = 0;
  let completed = true;
  for (const o of perOrder) {
    if (o.latenessS != null) totalLatenessS += Math.max(0, o.latenessS);
    if (!o.completed || o.endS == null) completed = false;
    else if (o.endS > lastEndS) lastEndS = o.endS;
  }
  return {
    totalLatenessS,
    totalSetupS,
    makespanS: completed ? lastEndS : Number.POSITIVE_INFINITY,
    completed,
  };
}

// Menor é melhor, com não-finitos SEMPRE ao fim.
function bySmaller(a: number, b: number): number {
  const aBad = !Number.isFinite(a);
  const bBad = !Number.isFinite(b);
  if (aBad || bBad) return aBad === bBad ? 0 : aBad ? 1 : -1;
  return a === b ? 0 : a - b;
}

// Concluir a fila é PRÉ-REQUISITO, não critério de desempate. Sem esta chave primária, atraso e
// setup premiariam quem NÃO termina: uma ordem que não coube no horizonte não reporta lateness
// (fica null) nem paga setup, então o candidato que "esquece" 3 das 5 OPs somaria menos que o
// que entrega todas — e apareceria no topo do ranking como a melhor sequência.
function byCompleteness(a: SequenceObjectiveEval, b: SequenceObjectiveEval): number {
  if (a.completed === b.completed) return 0;
  return a.completed ? -1 : 1;
}

function minutes(seconds: number): number | null {
  return Number.isFinite(seconds) ? Math.round(seconds / 60) : null;
}

/** Fábrica das 3 estratégias: todas comparam a mesma métrica, só muda de onde ela vem. */
function smallerIsBetter(
  id: SequenceObjective,
  label: string,
  view: (e: SequenceObjectiveEval) => ObjectiveMetricView,
  of: (e: SequenceObjectiveEval) => number
): SequenceObjectiveStrategy {
  return {
    id,
    label,
    // Fila parcial ⇒ métrica null: somar atraso/setup de uma sequência incompleta produziria um
    // número menor (e melhor) do que a realidade, porque as ordens que ficaram de fora não
    // contribuem. "—" é a leitura honesta; o candidato completo é que tem número comparável.
    metric: (e) => (e.completed ? view(e) : { ...view(e), value: null }),
    compare: (a, b) => byCompleteness(a, b) || bySmaller(of(a), of(b)),
    // Um candidato que não conclui a fila nunca ganha da base; uma base que não conclui é
    // superada por qualquer candidato que conclua (mesmo com métrica pior).
    beatsBaseline: (c, base) => {
      if (!c.completed) return false;
      if (!base.completed) return true;
      return bySmaller(of(c), of(base)) < 0;
    },
  };
}

const TOTAL_LATENESS = smallerIsBetter(
  "TOTAL_LATENESS",
  "Atraso total",
  (e) => ({ value: minutes(e.totalLatenessS), unit: "min", label: "Atraso total" }),
  (e) => e.totalLatenessS
);

const TOTAL_SETUP = smallerIsBetter(
  "TOTAL_SETUP",
  "Setup total",
  (e) => ({ value: minutes(e.totalSetupS), unit: "min", label: "Setup total" }),
  (e) => e.totalSetupS
);

const MAKESPAN = smallerIsBetter(
  "MAKESPAN",
  "Duração da fila",
  (e) => ({
    value: Number.isFinite(e.makespanS) ? Math.round((e.makespanS / 3600) * 10) / 10 : null,
    unit: "h",
    label: "Duração da fila",
  }),
  (e) => e.makespanS
);

export const SEQUENCE_OBJECTIVE_STRATEGIES: Record<SequenceObjective, SequenceObjectiveStrategy> = {
  TOTAL_LATENESS,
  TOTAL_SETUP,
  MAKESPAN,
};

const SEQUENCE_OBJECTIVES = Object.keys(SEQUENCE_OBJECTIVE_STRATEGIES) as SequenceObjective[];

/** Guard de kind: o objetivo pertence ao lote de SEQUÊNCIA? (o service valida nas duas direções) */
export function isSequenceObjective(objective: string): objective is SequenceObjective {
  return (SEQUENCE_OBJECTIVES as string[]).includes(objective);
}

export function getSequenceObjectiveStrategy(id: SequenceObjective): SequenceObjectiveStrategy {
  return SEQUENCE_OBJECTIVE_STRATEGIES[id];
}
